import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, RefreshCw, Download, X, CheckCircle2, AlertCircle, Loader2, Link2, Square, CheckSquare } from 'lucide-react';
import Card from './Card';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import { externalSourcesApi, type ComixChapter, type DownloadBatch } from '../api/externalSources';

interface Props {
  seriesId: string;
}

type FilterMode = 'new' | 'all';

export default function ExternalSourcesCard({ seriesId }: Props) {
  const queryClient = useQueryClient();
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('new');
  const [selectedChapterIds, setSelectedChapterIds] = useState<Set<number>>(new Set());
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  // ─── Queries ───────────────────────────────────────────────────
  const { data: sources, isLoading: sourcesLoading } = useQuery({
    queryKey: ['external-sources', seriesId],
    queryFn: () => externalSourcesApi.list(seriesId),
  });

  const source = sources?.[0]; // Currently we support one source per series

  const { data: batches } = useQuery({
    queryKey: ['download-batches', seriesId],
    queryFn: () => externalSourcesApi.listBatches(seriesId),
    refetchInterval: activeBatchId ? 3000 : false,
  });

  // Track active or recently failed batch
  const activeBatch = batches?.find(
    (b) => ['pending', 'downloading', 'translating', 'download_failed', 'translation_failed'].includes(b.status)
  );

  useEffect(() => {
    if (activeBatch) {
      setActiveBatchId(activeBatch.id);
    }
  }, [activeBatch?.id]);

  // Poll active batch for progress (only poll if batch is still running)
  const isActiveBatchRunning = activeBatch && ['pending', 'downloading', 'translating'].includes(activeBatch.status);

  const { data: batchProgress } = useQuery({
    queryKey: ['download-batch', seriesId, activeBatchId],
    queryFn: () => externalSourcesApi.getBatch(seriesId, activeBatchId!),
    enabled: !!activeBatchId,
    refetchInterval: isActiveBatchRunning ? 3000 : false,
  });

  // Keep chapters & jobs in sync while batch is active
  useEffect(() => {
    if (isActiveBatchRunning) {
      queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', seriesId] });
    }
  }, [isActiveBatchRunning, batchProgress, seriesId, queryClient]);

  // ─── Mutations ─────────────────────────────────────────────────
  const createSource = useMutation({
    mutationFn: (url: string) => externalSourcesApi.create(seriesId, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-sources', seriesId] });
      setShowLinkInput(false);
      setLinkUrl('');
    },
  });

  const refreshChapters = useMutation({
    mutationFn: () => externalSourcesApi.refresh(seriesId, source!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-sources', seriesId] });
    },
  });

  const updateSource = useMutation({
    mutationFn: (patch: { preferred_group_id?: number; preferred_group_name?: string }) =>
      externalSourcesApi.update(seriesId, source!.id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-sources', seriesId] });
    },
  });

  const deleteSource = useMutation({
    mutationFn: () => externalSourcesApi.delete(seriesId, source!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-sources', seriesId] });
    },
  });

  const startDownload = useMutation({
    mutationFn: (chapterIds: number[]) => externalSourcesApi.download(seriesId, source!.id, chapterIds),
    onSuccess: (data) => {
      setActiveBatchId(data.batch_id);
      setSelectedChapterIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['download-batches', seriesId] });
      // Invalidate chapters & jobs so new records appear in Chapters card
      queryClient.invalidateQueries({ queryKey: ['chapters', seriesId] });
      queryClient.invalidateQueries({ queryKey: ['jobs', seriesId] });
    },
  });

  const cancelBatch = useMutation({
    mutationFn: (batchId: string) => externalSourcesApi.cancelBatch(seriesId, batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['download-batches', seriesId] });
      setActiveBatchId(null);
    },
  });

  // ─── Derived data ──────────────────────────────────────────────
  const chapters = source?.chapters_cache?.chapters ?? [];
  const groups = source?.chapters_cache?.groups ?? [];

  const filteredChapters = chapters.filter((ch) => {
    // Filter by preferred group
    if (source?.preferred_group_id && ch.scanlation_group_id !== source.preferred_group_id) {
      return false;
    }
    // Filter mode
    if (filterMode === 'new') return ch.is_new;
    return true;
  });

  const newCount = chapters.filter((ch) => {
    if (source?.preferred_group_id && ch.scanlation_group_id !== source.preferred_group_id) return false;
    return ch.is_new;
  }).length;

  // ─── Handlers ──────────────────────────────────────────────────
  const toggleChapter = useCallback((chapterId: number) => {
    setSelectedChapterIds((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) next.delete(chapterId);
      else next.add(chapterId);
      return next;
    });
  }, []);

  const selectAllNew = useCallback(() => {
    const newIds = chapters
      .filter((ch) => {
        if (source?.preferred_group_id && ch.scanlation_group_id !== source.preferred_group_id) return false;
        return ch.is_new;
      })
      .map((ch) => ch.chapter_id);
    setSelectedChapterIds(new Set(newIds));
  }, [chapters, source?.preferred_group_id]);

  const handleGroupChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = e.target.value;
      if (val === '') {
        updateSource.mutate({ preferred_group_id: 0, preferred_group_name: '' });
      } else {
        const group = groups.find((g) => g.scanlation_group_id === Number(val));
        if (group) {
          updateSource.mutate({
            preferred_group_id: group.scanlation_group_id,
            preferred_group_name: group.name,
          });
        }
      }
      setSelectedChapterIds(new Set());
    },
    [groups, updateSource]
  );

  // ─── Loading state ────────────────────────────────────────────
  if (sourcesLoading) {
    return (
      <Card title="External Sources" className="hover:shadow-md transition-shadow">
        <LoadingSpinner />
      </Card>
    );
  }

  // ─── No source linked ────────────────────────────────────────
  if (!source) {
    return (
      <Card title="External Sources" className="hover:shadow-md transition-shadow">
        {showLinkInput ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Вставьте ссылку на мангу с comix.to
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://comix.to/title/..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Button
                size="sm"
                onClick={() => createSource.mutate(linkUrl)}
                disabled={!linkUrl.trim() || createSource.isPending}
              >
                {createSource.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Привязать'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowLinkInput(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            {createSource.isError && (
              <p className="text-sm text-red-500">
                {(createSource.error as any)?.response?.data?.error ?? 'Ошибка привязки'}
              </p>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <Globe className="h-10 w-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Нет привязанных источников
            </p>
            <Button size="sm" onClick={() => setShowLinkInput(true)}>
              <Link2 className="h-4 w-4 mr-2" />
              Привязать источник
            </Button>
          </div>
        )}
      </Card>
    );
  }

  // ─── Source linked ────────────────────────────────────────────
  const isProcessing = !!activeBatch && ['pending', 'downloading', 'translating'].includes(activeBatch.status);
  const batch = batchProgress ?? activeBatch;

  return (
    <Card title="External Sources" className="hover:shadow-md transition-shadow">
      {/* Source info header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {source.manga_title || 'Manga'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{source.url}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => refreshChapters.mutate()}
            disabled={refreshChapters.isPending || isProcessing}
            title="Обновить список глав"
          >
            <RefreshCw className={`h-4 w-4 ${refreshChapters.isPending ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (confirm('Удалить привязку к источнику?')) deleteSource.mutate();
            }}
            disabled={isProcessing}
            title="Удалить привязку"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Active batch progress */}
      {batch && ['pending', 'downloading', 'translating', 'download_failed', 'translation_failed'].includes(batch.status) && (
        <BatchProgressSection batch={batch} onCancel={() => cancelBatch.mutate(batch.id)} />
      )}

      {/* Chapters list */}
      {chapters.length > 0 && !isProcessing && (
        <>
          {/* Controls bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            {/* Group selector */}
            {groups.length > 1 && (
              <select
                value={source.preferred_group_id ?? ''}
                onChange={handleGroupChange}
                className="text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Все провайдеры</option>
                {groups.map((g) => (
                  <option key={g.scanlation_group_id} value={g.scanlation_group_id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}

            {/* Filter toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  filterMode === 'new'
                    ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                onClick={() => setFilterMode('new')}
              >
                Новые ({newCount})
              </button>
              <button
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  filterMode === 'all'
                    ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                onClick={() => setFilterMode('all')}
              >
                Все ({chapters.length})
              </button>
            </div>

            <div className="flex-1" />

            <Button size="sm" variant="secondary" onClick={selectAllNew} disabled={newCount === 0}>
              Выбрать все новые
            </Button>
          </div>

          {/* Chapter list */}
          <div className="max-h-80 overflow-y-auto space-y-1">
            {filteredChapters.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                {filterMode === 'new' ? 'Нет новых глав' : 'Нет глав'}
              </p>
            ) : (
              filteredChapters.map((ch) => (
                <ChapterRow
                  key={ch.chapter_id}
                  chapter={ch}
                  selected={selectedChapterIds.has(ch.chapter_id)}
                  onToggle={() => toggleChapter(ch.chapter_id)}
                />
              ))
            )}
          </div>

          {/* Download button */}
          {selectedChapterIds.size > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Выбрано: {selectedChapterIds.size}
              </p>
              <Button
                onClick={() => startDownload.mutate(Array.from(selectedChapterIds))}
                disabled={startDownload.isPending}
              >
                {startDownload.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Скачать и перевести
              </Button>
            </div>
          )}
          {startDownload.isError && (
            <p className="mt-2 text-sm text-red-500">
              {(startDownload.error as any)?.response?.data?.error ?? 'Ошибка запуска'}
            </p>
          )}
        </>
      )}

      {/* No cache yet */}
      {!chapters.length && !isProcessing && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Нажмите "Обновить" для загрузки списка глав
          </p>
          <Button
            size="sm"
            onClick={() => refreshChapters.mutate()}
            disabled={refreshChapters.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshChapters.isPending ? 'animate-spin' : ''}`} />
            Загрузить список глав
          </Button>
        </div>
      )}

      {/* Last cache update */}
      {source.chapters_cache_updated_at && (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Обновлено: {new Date(source.chapters_cache_updated_at).toLocaleString('ru-RU')}
        </p>
      )}
    </Card>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function ChapterRow({
  chapter,
  selected,
  onToggle,
}: {
  chapter: ComixChapter;
  selected: boolean;
  onToggle: () => void;
}) {
  const formatDate = (ts: number | null) => {
    if (!ts) return '';
    return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
        selected
          ? 'bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-transparent'
      }`}
    >
      {selected ? (
        <CheckSquare className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
      ) : (
        <Square className="h-4 w-4 text-gray-400 flex-shrink-0" />
      )}

      <span className="text-sm font-medium text-gray-900 dark:text-white w-16 flex-shrink-0">
        Ch {chapter.number % 1 === 0 ? Math.floor(chapter.number) : chapter.number}
      </span>

      {chapter.volume > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
          Vol {chapter.volume}
        </span>
      )}

      {chapter.scanlation_group_name && (
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {chapter.scanlation_group_name}
        </span>
      )}

      <span className="flex-1" />

      {chapter.is_new && (
        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400">
          NEW
        </span>
      )}

      <span className="text-xs text-gray-400">{formatDate(chapter.created_at)}</span>
    </button>
  );
}

function BatchProgressSection({ batch, onCancel }: { batch: DownloadBatch; onCancel: () => void }) {
  const progress = batch.chapters_progress ?? {};
  const requested = batch.chapters_requested ?? [];
  const total = requested.length;

  const statusLabels: Record<string, string> = {
    pending: 'Ожидание...',
    downloading: 'Скачивание...',
    translating: 'Перевод...',
    completed: 'Завершено',
    download_failed: 'Ошибка скачивания',
    translation_failed: 'Ошибка перевода',
    cancelled: 'Отменено',
  };

  const isFailed = batch.status === 'download_failed' || batch.status === 'translation_failed';
  const isActive = ['pending', 'downloading', 'translating'].includes(batch.status);

  const downloadedCount = Object.values(progress).filter((p) => p.download_status === 'completed').length;
  const translatedCount = Object.values(progress).filter((p) => p.translation_status === 'completed').length;

  const overallPercent = total > 0
    ? Math.round(((downloadedCount * 0.3 + translatedCount * 0.7) / total) * 100)
    : 0;

  return (
    <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isActive && <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
          {isFailed && <AlertCircle className="h-4 w-4 text-red-500" />}
          {batch.status === 'completed' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {statusLabels[batch.status] ?? batch.status}
          </span>
        </div>
        {isActive && (
          <Button size="sm" variant="danger" onClick={onCancel}>
            Отменить
          </Button>
        )}
        {isFailed && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Убрать"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Overall progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${isFailed ? 'bg-red-500' : 'bg-primary-500'}`}
          style={{ width: `${overallPercent}%` }}
        />
      </div>

      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Скачано: {downloadedCount}/{total} | Переведено: {translatedCount}/{total}
      </div>

      {batch.error_message && (
        <details className="group">
          <summary className="flex items-center gap-1.5 cursor-pointer text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 select-none">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{batch.error_message.length > 80 ? batch.error_message.slice(0, 80) + '…' : batch.error_message}</span>
          </summary>
          <pre className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-words max-h-40 overflow-y-auto border border-red-200 dark:border-red-800">
            {batch.error_message}
          </pre>
        </details>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Детальный прогресс по главам отображается в карточке «Главы»
      </p>
    </div>
  );
}
